import { useEffect } from "react";

/**
 * Global drag-to-scroll: while an HTML5 drag is in progress, if the cursor
 * approaches the top/bottom edge of the viewport, scroll the page so the
 * user can reach offscreen drop targets (e.g. folder rails at the top).
 */
export function useDragAutoScroll({
  edgeSize = 90,
  maxSpeed = 22,
} = {}) {
  useEffect(() => {
    let raf = null;
    let speed = 0;

    const tick = () => {
      if (speed === 0) { raf = null; return; }
      window.scrollBy(0, speed);
      raf = requestAnimationFrame(tick);
    };

    const onDragOver = (e) => {
      const y = e.clientY;
      const h = window.innerHeight;
      let next = 0;
      if (y < edgeSize) {
        next = -Math.ceil(maxSpeed * (1 - y / edgeSize));
      } else if (y > h - edgeSize) {
        next = Math.ceil(maxSpeed * (1 - (h - y) / edgeSize));
      }
      speed = next;
      if (speed !== 0 && raf == null) raf = requestAnimationFrame(tick);
    };

    const stop = () => {
      speed = 0;
      if (raf != null) { cancelAnimationFrame(raf); raf = null; }
    };

    document.addEventListener("dragover", onDragOver);
    document.addEventListener("dragend", stop);
    document.addEventListener("drop", stop, true);
    window.addEventListener("blur", stop);

    return () => {
      document.removeEventListener("dragover", onDragOver);
      document.removeEventListener("dragend", stop);
      document.removeEventListener("drop", stop, true);
      window.removeEventListener("blur", stop);
      if (raf != null) cancelAnimationFrame(raf);
    };
  }, [edgeSize, maxSpeed]);
}
